import React, { useState, Suspense, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Frames, CardFrame } from 'frames-react';

import { Button, Checkbox, Input } from 'geniebook-ui/dist';
import { ErrorObject } from '@models/interfaces/global.interface';
import { IPaymentReq } from '@models/interfaces/mock_exam.interface';
import MockExamService from '@services/MockExamServices.service';
import {
  CURRENCY_CODE_VIETNAM,
  ICurrencySymbols,
  currencySymbols,
} from '@utils/constants/mock-exam.constants';
import { NumberToFormattedString } from '@utils/helpers/formatNumber';

export interface IPaymentFormData {
  parentInfoId: string;
  currencyCode: string;
  amountPaid: number;
  classIds: number[];
  address: string;
  postalCode: string;
}

const PaymentForm = ({
  initialPaymentData,
  setError,
}: {
  initialPaymentData: IPaymentFormData;
  setError: React.Dispatch<React.SetStateAction<ErrorObject>>;
}) => {
  const [paymentData, setPaymentData] =
    useState<IPaymentFormData>(initialPaymentData);
  const [isCheckedNoRefund, setIsCheckedNoRefund] = useState<boolean>(false);
  const [isSubmitCard, setIsSubmitCard] = useState<boolean>(false);
  const [isCardValid, setIsCardValid] = useState<boolean>(
    paymentData.currencyCode == CURRENCY_CODE_VIETNAM,
  );
  const refAddress = useRef<HTMLInputElement>(null);
  const refPostalCode = useRef<HTMLInputElement>(null);

  const requestPaymentMutation = useMutation(
    (requestData: IPaymentReq) =>
      MockExamService.requestPaymentLink(requestData),
    {
      onSuccess(data) {
        if (data.data.error == true) {
          Frames.init();
          setIsSubmitCard(false);
          setError({
            error: true,
            error_msg: data.data.error_msg,
            error_code: '',
            error_state: 'error',
          });
        } else {
          window.location.href = data.data.redirect_url;
        }
      },
      onError(error: Error) {
        Frames.init();
        setIsSubmitCard(false);
        setError({
          error: true,
          error_msg: error.message,
          error_code: '',
        });
      },
    },
  );

  const hanldeRequestPayment = (token: string) => {
    requestPaymentMutation.mutate({
      parent_info_id: paymentData.parentInfoId,
      token: token,
      currency_code: paymentData.currencyCode,
      amount_paid: paymentData.amountPaid,
      class_ids: paymentData.classIds,
      address: refAddress.current?.value + '',
      postal_code: refPostalCode.current?.value + '',
    });
  };

  const isSubmitButtonDisabled = () => {
    return (
      !isCheckedNoRefund ||
      !isCardValid ||
      requestPaymentMutation.isLoading ||
      isSubmitCard ||
      !paymentData.address ||
      !paymentData.postalCode
    );
  };

  const handleClickPayment = () => {
    if (paymentData.currencyCode == CURRENCY_CODE_VIETNAM)
      hanldeRequestPayment('');
    else Frames.submitCard();
  };

  return (
    <>
      <div className="webinar-payment-title">
        <div className="">Total Payment</div>
        <div className="ml-auto">
          {currencySymbols[paymentData.currencyCode as keyof ICurrencySymbols] +
            NumberToFormattedString(paymentData.amountPaid)}
        </div>
      </div>
      <div className="flex flex-col gap-0 overflow-x-hidden -mx-5 px-5">
        <div className="webinar-address-container">
          <Input
            mode={'light'}
            variant={'outside'}
            placeholder="Enter your address"
            className="w-full register-input mb-6"
            label={'Address'}
            value={paymentData.address}
            disabled={requestPaymentMutation.isLoading || isSubmitCard}
            maxLength={64}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              paymentData.address = e.currentTarget.value;
              const tempPaymentData: IPaymentFormData = JSON.parse(
                JSON.stringify(paymentData),
              );
              setPaymentData(tempPaymentData);
            }}
            ref={refAddress}
          />
          <Input
            mode={'light'}
            variant={'outside'}
            placeholder="Enter your postal code"
            className="w-full register-input mb-6"
            label={'Postal Code'}
            value={paymentData.postalCode}
            disabled={requestPaymentMutation.isLoading || isSubmitCard}
            maxLength={10}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              paymentData.postalCode = e.currentTarget.value;
              const tempPaymentData: IPaymentFormData = JSON.parse(
                JSON.stringify(paymentData),
              );
              setPaymentData(tempPaymentData);
            }}
            ref={refPostalCode}
          />
        </div>
        {paymentData.currencyCode != CURRENCY_CODE_VIETNAM && (
          <>
            <p className="webinar-payment-label">Payment</p>
            <div className="webinar-payment-frame-container">
              <Frames
                config={{
                  publicKey: `${process.env.CHECKOUT_PUBLIC_KEY}`,
                }}
                cardValidationChanged={() =>
                  setIsCardValid(Frames.isCardValid())
                }
                cardSubmitted={() => {
                  setIsSubmitCard(true);
                }}
                cardTokenized={e => {
                  // call service here
                  hanldeRequestPayment(e.token);
                }}
              >
                <CardFrame className="h-6 my-2" />
              </Frames>
            </div>
          </>
        )}

        <div className="webinar-payment-checkbox-container">
          <Checkbox
            className="cursor-pointer"
            variant={'light'}
            isChecked={isCheckedNoRefund}
            isValidation={false}
            onChange={() => {
              setIsCheckedNoRefund(!isCheckedNoRefund);
            }}
            label={''}
            disabled={requestPaymentMutation.isLoading}
          />
          <div
            onClick={() => {
              setIsCheckedNoRefund(!isCheckedNoRefund);
            }}
          >
            I acknowledge that this purchase is not refundable.
          </div>
        </div>
        <div>
          <Suspense>
            <button
              className="w-full mt-6 max-lg:hidden"
              onClick={() => handleClickPayment()}
              disabled={isSubmitButtonDisabled()}
            >
              <Button
                className="!max-w-none w-full px-6"
                variant="primary"
                disabled={isSubmitButtonDisabled()}
              >
                {requestPaymentMutation.isLoading
                  ? 'Processing'
                  : paymentData.currencyCode == CURRENCY_CODE_VIETNAM
                  ? 'Continue to Payment'
                  : 'Continue'}
              </Button>
            </button>
            <p className="mt-6 font-medium text-xs text-neutral-pencil max-lg:hidden">
              By clicking continue, you have agreed to Geniebook's{' '}
              <a
                id="link"
                href="https://geniebook.com/terms"
                target="_blank"
                className="text-primary-apricot underline font-medium hover:text-primary-skyblue"
                rel="noreferrer"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                href="https://geniebook.com/policies/privacy"
                target="_blank"
                className="text-primary-apricot underline font-medium hover:text-primary-skyblue"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
          </Suspense>
        </div>
        <div className="webinar-payment-floating-footer">
          <div className="webinar-payment-detail">
            <p className="">Total Payment</p>
            <p className="ml-auto">
              {currencySymbols[
                paymentData.currencyCode as keyof ICurrencySymbols
              ] + NumberToFormattedString(paymentData.amountPaid)}
            </p>
          </div>
          <button
            className="w-full"
            onClick={() => handleClickPayment()}
            disabled={isSubmitButtonDisabled()}
          >
            <Button
              className="!max-w-none w-full"
              variant="primary"
              disabled={isSubmitButtonDisabled()}
            >
              {requestPaymentMutation.isLoading
                ? 'Processing'
                : paymentData.currencyCode == CURRENCY_CODE_VIETNAM
                ? 'Continue to Payment'
                : 'Continue'}
            </Button>
          </button>
          <p>
            By clicking continue, you have agreed to Geniebook's{' '}
            <a
              id="link"
              href="https://geniebook.com/terms"
              target="_blank"
              className="text-primary-apricot underline font-medium hover:text-primary-skyblue"
              rel="noreferrer"
            >
              Terms
            </a>{' '}
            and{' '}
            <a
              href="https://geniebook.com/policies/privacy"
              target="_blank"
              className="text-primary-apricot underline font-medium hover:text-primary-skyblue"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
};

export default PaymentForm;
